import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, CheckCircle2, Clock3, ShieldCheck, FileCheck2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_LINK, EMAIL } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinalCTA() {
  const [form, setForm] = useState({
    company: "",
    phone: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.phone) {
      toast.error("Merci d'indiquer au moins votre société et votre téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, {
        name: form.company,
        company: form.company,
        phone: form.phone,
        city: form.city,
        message: form.message,
      });
      setDone(true);
      toast.success("Demande envoyée ! Nous vous rappelons sous 24h.");
      setForm({ company: "", phone: "", city: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="devis"
      className="relative py-20 md:py-32 bg-gradient-to-br from-blue-700 via-blue-600 to-blue-700 text-white overflow-hidden"
      data-testid="final-cta-section"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-400/30 rounded-full blur-3xl" aria-hidden />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-slate-900/30 rounded-full blur-3xl" aria-hidden />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT */}
          <div className="lg:col-span-6">
            <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-blue-200">
              Demande de devis
            </div>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]" data-testid="cta-title">
              Libérez vos archives.<br />Sans effort.
            </h2>
            <p className="mt-5 text-lg md:text-xl text-blue-100 leading-relaxed max-w-xl">
              Devis gratuit, sans engagement. Intervention sous 24/48h dans tous les Bouches-du-Rhône.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Clock3, text: "Réponse < 24h" },
                { icon: ShieldCheck, text: "Confidentialité totale" },
                { icon: FileCheck2, text: "Certificat inclus" },
                { icon: CheckCircle2, text: "Vous ne touchez à rien" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-sm md:text-base text-white">
                  <Icon className="h-5 w-5 text-blue-200" />
                  <span className="font-semibold">{text}</span>
                </div>
              ))}
            </div>

            {/* Big phone block */}
            <div className="mt-10 p-6 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm">
              <div className="text-sm text-blue-100 font-bold uppercase tracking-wider">Plus rapide : appelez-nous</div>
              <a
                href={`tel:${PHONE_LINK}`}
                className="mt-2 flex items-center gap-3 text-3xl md:text-4xl font-display font-extrabold hover:text-blue-200 transition-colors tracking-tight"
                data-testid="cta-phone-link"
              >
                <Phone className="h-7 w-7" />
                {PHONE_DISPLAY}
              </a>
              <div className="mt-3 flex items-center gap-2 text-sm text-blue-100">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${EMAIL}`} className="hover:text-white" data-testid="cta-email-link">{EMAIL}</a>
              </div>
            </div>
          </div>

          {/* RIGHT — SHORT FORM */}
          <div className="lg:col-span-6">
            {done ? (
              <div className="bg-white rounded-3xl p-10 text-center shadow-2xl" data-testid="form-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                </div>
                <h3 className="font-display mt-5 text-2xl font-bold text-slate-950">Demande envoyée !</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Merci. Notre équipe vous rappelle sous <strong className="text-slate-950">24h ouvrées</strong>.
                </p>
                <a
                  href={`tel:${PHONE_LINK}`}
                  className="mt-6 inline-flex items-center gap-2 bg-slate-950 text-white font-bold py-3 px-6 rounded-full hover:bg-blue-600 transition-colors"
                  data-testid="success-call-button"
                >
                  <Phone className="h-4 w-4" />
                  Urgent ? Appeler {PHONE_DISPLAY}
                </a>
                <button
                  onClick={() => setDone(false)}
                  className="mt-4 block mx-auto text-sm text-slate-500 hover:text-slate-900 underline"
                  data-testid="form-reset-button"
                >
                  Envoyer une nouvelle demande
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl space-y-4"
                data-testid="quote-form"
              >
                <div className="text-slate-950">
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">Devis rapide en 60 secondes</h3>
                  <p className="text-sm text-slate-500 mt-1">4 champs. Réponse sous 24h.</p>
                </div>

                <Field name="company" label="Nom société *" value={form.company} onChange={onChange} placeholder="Cabinet Martin / Étude X..." />
                <Field name="phone" label="Téléphone *" type="tel" value={form.phone} onChange={onChange} placeholder="06 12 34 56 78" />
                <Field name="city" label="Ville" value={form.city} onChange={onChange} placeholder="Marseille, Aix, Vitrolles..." />

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    Votre besoin
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Ex: ~40 cartons en cave, intervention rapide souhaitée..."
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
                    data-testid="form-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 text-white font-bold py-4 px-6 rounded-2xl hover:bg-blue-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-base"
                  data-testid="form-submit-button"
                >
                  {loading ? "Envoi en cours..." : "Recevoir mon devis gratuit"}
                  {!loading && <ArrowRight className="h-5 w-5" />}
                </button>

                <p className="text-[11px] text-slate-500 text-center">
                  Données confidentielles · Aucun spam · Réponse sous 24h ouvrées.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ name, label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 text-sm font-medium placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
        data-testid={`form-${name}`}
      />
    </div>
  );
}
